import { useAuth } from 'contexts';
import { useResponse } from 'contexts/response';
import { useCallback } from 'react';
import { Role } from 'types';
import { Column, TableType } from 'types/data-table';

const AUTH_URL = new URL('/api/auth', window.location.origin);
const USER_URL = new URL('/api/users', window.location.origin);

export const useUserAPI = () => {
  const { handleError } = useResponse();
  const { setUserProfile } = useAuth();

  const resolveErrorStatus = useCallback(
    (response: Response, json: any) => {
      switch (response.status) {
        case 500:
          handleError('INTERNAL SERVER ERROR');
          return null;
        case 403:
          handleError('FORBIDDEN');
          return null;
        case 401:
          handleError('UNAUTHORIZED');
          return null;
        default:
          handleError(json.message ?? response.statusText);
          return null;
      }
    },
    [handleError]
  );

  const resetUserPassword = useCallback(
    async (username: string): Promise<string | null> => {
      const options: RequestInit = {
        method: 'PUT',
        headers: new Headers({ Authorization: `Bearer ${localStorage.getItem('token')}` })
      };

      try {
        const response = await fetch(`${AUTH_URL}/reset-password/${username}`, options);
        const text = await response.text();

        await Promise.allSettled([response, text]);

        if (response.ok) {
          return text;
        }

        return resolveErrorStatus(response, text);
      } catch (e) {
        handleError(e);
        return null;
      }
    },
    [handleError, resolveErrorStatus]
  );

  const updateUserColumns = useCallback(
    async (updatedColumns: Column[], table: TableType) => {
      const options: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }),
        body: JSON.stringify(
          updatedColumns.map(({ field, width }) => {
            return { column: field, width };
          })
        )
      };
      const url = new URL(`${USER_URL}/columns`, window.location.origin);
      url.searchParams.append('table', table.toUpperCase());

      try {
        const response = await fetch(url, options);
        const json = await response.json();
        await Promise.allSettled([response, json]);
        if (response.ok) {
          const hasRole = Object.values(Role).find((role: string) => role === json.role.toUpperCase());
          setUserProfile({
            username: json.username,
            email: json.email,
            role: hasRole ?? Role.NONE,
            active: json.active,
            customers: json.customers,
            truckingCompanies: json.truckingCompanies,
            locations: json.locations,
            defaultLocation: json.defaultLocation,
            columnPreferences: json.columnPreferences,
            savedFiltersJson: json.savedFiltersJson
          });
          return json;
        }
        return resolveErrorStatus(response, json);
      } catch (e) {
        return handleError(e);
      }
    },
    [handleError, resolveErrorStatus, setUserProfile]
  );

  const updateUserSavedFilters = useCallback(
    async (savedFiltersJson: string) => {
      const options: RequestInit = {
        method: 'PUT',
        headers: new Headers({
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }),
        body: savedFiltersJson
      };
      const url = new URL(`${USER_URL}/saved-filters`, window.location.origin);

      try {
        const response = await fetch(url, options);
        const json = await response.json();
        await Promise.allSettled([response, json]);
        if (response.ok) {
          const hasRole = Object.values(Role).find((role: string) => role === json.role.toUpperCase());
          setUserProfile({
            username: json.username,
            email: json.email,
            role: hasRole ?? Role.NONE,
            active: json.active,
            customers: json.customers,
            truckingCompanies: json.truckingCompanies,
            locations: json.locations,
            defaultLocation: json.defaultLocation,
            columnPreferences: json.columnPreferences,
            savedFiltersJson: json.savedFiltersJson
          });
          return json;
        }
        return resolveErrorStatus(response, json);
      } catch (e) {
        return handleError(e);
      }
    },
    [handleError, resolveErrorStatus, setUserProfile]
  );

  return { resetUserPassword, updateUserColumns, updateUserSavedFilters };
};
